/**
 * The signed-in home's next session (web-account-pages, signed-in-home.html
 * Variant B Band 4, the accent pass): the service photo beside the session's
 * name, status, day and time, who and where, View booking (the home's only
 * filled button) and Join session or Directions.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'next-session',
  description:
    "The client's next booked session on the signed-in home: its photo, name, status, when and where, View booking, and Join session or Directions.",
  placeable: true,
  options: {
    heading: {
      type: 'string',
      default: null,
      maxLength: 80,
      description: "The heading above the session; null for none, as on the standard home, where the welcome line says it.",
    },
  },
  parts: {
    session: { description: 'The whole next session.', required: false },
    heading: { description: 'The heading above the session, only when the heading option is set.', required: false, role: 'title' },
    main: { description: 'The photo and the details.', required: false },
    details: { description: 'The name, when and where.', required: false },
    photo: { description: "The service's photo.", required: false, role: 'photo' },
    name: { description: "The service's name.", required: false, role: 'title' },
    status: { description: 'Confirmed, Pending approval, Payment due.', required: false },
    when: { description: 'The day and time, and the length.', required: false },
    where: { description: 'Who with, and where or online.', required: false },
    viewBooking: { description: "View booking, to the booking's page.", required: true, role: 'primary-action' },
    join: { description: 'Join session: opens the meeting link of a confirmed online session.', required: false, role: 'secondary-action' },
    directions: { description: 'Directions: opens the place in maps.', required: false, role: 'secondary-action' },
  },
  actions: {
    open: { description: "Opens a page of the site (View booking opens the booking's page).", args: [{ name: 'href', kind: 'value', type: 'string', format: 'path' }] },
  },
  events: {},
});

/** The next session as drawn. */
export interface NextSessionBooking {
  /** The booking's id. */
  readonly id: string;
  /** The service's name. */
  readonly name: string;
  /** The service's photo, or null. */
  readonly photoUrl: string | null;
  /** website-photo-zoom: the same photo framed as the studio set it (crop point and zoom), or null. */
  readonly photo: SitePhoto | null;
  /** Confirmed, Pending approval, Payment due, Forms due, Waitlisted. */
  readonly status: string;
  /** "Today, Oct 16 at 2:00 PM" or "Sat, Oct 24 at 2:00 PM". */
  readonly when: string;
  /** ", 75 minutes", or empty for a group event. */
  readonly length: string;
  /** "With Noor Hadley at Juniper & Stone, 41 Exchange St" or "With Noor Hadley, online on Zoom". */
  readonly where: string;
  /** The booking's page. */
  readonly href: string;
  /** The meeting link of a confirmed online session, or null. */
  readonly joinHref: string | null;
  /** The place in maps for an in-person session, or null. */
  readonly directionsHref: string | null;
}

/** What the next session knows. */
export interface NextSessionState {
  /** The client's next booking, or null when nothing is booked. */
  readonly booking: NextSessionBooking | null;
}

/** What a drawing can ask the next session to do. */
export interface NextSessionActions {
  /** Open a page of the site. */
  readonly open: (href: string) => void;
}

/** The next session tells its host nothing. */
export type NextSessionEvents = Record<string, never>;
